import React, { useState } from 'react';
import { useParams } from 'react-router-dom'
import { useApis } from '../../api/useApiClient';
import { useNotification } from '../../context/NotificationContext';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

import LinearProgress from '@mui/material/LinearProgress';

export default function Form({
    resourceName = "Item", // used in success / error messages: Eg: Customer
    apiPath, // API resource path to submit data: eg: /customer
    formData = {},
    requiredFields = [], // array of required field names and respective labels: eg: [{ name: 'name', label: 'Name' }, { name: 'email', label: 'Email' }]
    editMode = false, // set true for edit mode (with PATCH method);
    submitText = "Submit",
    onSubmit = (data) => { console.debug('Item submitted', data) }, // callback on form submit. Allow parent to perform actions based on the submit response (such as refresh the page or redirect to the created item's page)
    cancelText = "Cancel",
    onCancel = () => { console.debug('Form cancelled') }, // callback on form cancel

    disabled = false,
    children
}) {

    const { tenantApi } = useApis();
    const { showNotification } = useNotification();

    const { id } = useParams(); // Get the item ID from the URL parameters if provided (in edit mode)

    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // prevent concurrent invocations due to UI bugs
        if (loading) {
            console.debug("Can't submit the form again while loading. handleSubmit exit");
            return
        }

        // Basic form validation for required fields
        for (const field of requiredFields) {
            if (!formData[field.name] || formData[field.name].toString().trim() === '') {
                showNotification(`${field.label} is required`, 'warning');
                return;
            }
        }

        // Sanitize and prepare the form data before sending
        // 1. Skip any fields with null / undefined values
        const sanitizedData = {};
        Object.keys(formData).forEach(key => {
            if (formData[key] !== null && formData[key] !== undefined) {
                sanitizedData[key] = formData[key];
            }
        });

        // 2. Trim string values
        Object.keys(sanitizedData).forEach(key => {
            if (typeof sanitizedData[key] === 'string') {
                sanitizedData[key] = sanitizedData[key].trim();
            }
        });

        // 3. Remove empty strings
        Object.keys(sanitizedData).forEach(key => {
            if (sanitizedData[key] === '') {
                delete sanitizedData[key];
            }
        });

        // Now use the sanitizedData for submission
        console.debug("Sanitized form data ready for submission:", sanitizedData);

        // --- End of data sanitization

        setLoading(true);

        const path = editMode ? `${apiPath}/${id}` : `${apiPath}`;
        const method = editMode ? "PATCH" : "POST";

        // Dynamic success || error message generation based on create or edit mode
        const successMessage = editMode ? `${resourceName} updated successfully` : `${resourceName} created successfully`;
        const errorMessage = editMode ? `${resourceName} update failed` : `${resourceName} creation failed`;

        console.debug("Submit the form data ...", formData, path, method);
        // return;

        try {
            const res = await tenantApi(path, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sanitizedData)
            });

            console.debug(successMessage);
            showNotification(successMessage, 'success');
            onSubmit(res?.data);

        } catch (e) {
            console.error(e);
            showNotification(
                e.message || errorMessage,
                "error"
            );
        }
        finally {
            setLoading(false);
        }
    };


    // Handle form cancel action dynamically based on add-mode or edit-mode
    function handleCancel() {
        onCancel();
    }

    return (
        <Box>
            <Typography variant='h6' sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} mb={4}>
                {editMode ? `Update ${resourceName} details` : `Add New ${resourceName}`}
            </Typography>

            <form onSubmit={handleSubmit}>


                {children}

                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ marginTop: 2 }}>
                    <Button variant="outlined" color="primary" onClick={handleCancel} disabled={loading}>
                        {cancelText}
                    </Button>
                    <Button variant="contained" color="primary" type="submit" disabled={loading || disabled}>
                        {submitText}
                    </Button>
                </Stack>

                {loading ? <LinearProgress sx={{ marginTop: 2 }} /> : null}
            </form>
        </Box>
    );
};
